/**
 * Shared component utilities. Used internally by all panel components.
 */

import type { Vault } from '../types.ts';
import { store } from '../store.ts';

export type ShadowEl = HTMLElement & { shadowRoot: ShadowRoot };

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function withVault(host: HTMLElement, paint: (v: Vault, root: ShadowRoot) => void): () => void {
  const root = host.shadowRoot;
  if (!root) return () => {};
  return store.subscribe((s) => {
    if (s.phase === 'ready' && s.vault) paint(s.vault, root);
  });
}

export function injectCommonStyles(root: ShadowRoot): void {
  const style = document.createElement('style');
  style.textContent = `
    :host {
      display: block;
      font-family: var(--body, 'EB Garamond', Georgia, serif);
      color: var(--ink, oklch(28% 0.020 50));
      contain: layout style;
    }
    * { box-sizing: border-box; }
    .frame {
      background: var(--parchment-warm);
      border: 1px solid var(--rule-strong);
      border-radius: 2px;
      padding: 22px 20px;
      position: relative;
      box-shadow:
        0 1px 0 oklch(50% 0.040 50 / 0.06),
        inset 0 1px 0 oklch(100% 0.012 80 / 0.55);
    }
    .frame::before {
      content: '';
      position: absolute;
      inset: 4px;
      border: 0.5px solid var(--rule-hair);
      border-radius: 1px;
      pointer-events: none;
    }
    h2 {
      font-family: var(--display);
      font-weight: 600;
      font-size: 22px;
      color: var(--indigo);
      letter-spacing: -0.01em;
      margin: 0 0 4px;
      display: flex;
      align-items: baseline;
      gap: 10px;
    }
    h2 .deva { color: var(--ink); font-size: 22px; font-weight: 400; opacity: 0.85; font-family: var(--hindi); }
    .kicker {
      font-family: var(--mono);
      font-size: 9.5px;
      letter-spacing: 0.32em;
      text-transform: uppercase;
      color: var(--ink-faint);
      margin-bottom: 14px;
    }
    .pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 3px 10px;
      border: 1px solid var(--rule-strong);
      border-radius: 99px;
      font-family: var(--mono);
      font-size: 9.5px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      background: var(--parchment);
      color: var(--ink-dim);
    }
    .pill.indigo { color: var(--indigo); border-color: var(--rule-indigo); background: var(--indigo-wash); }
    .pill.crimson { color: var(--crimson); border-color: oklch(48% 0.180 25 / 0.34); background: oklch(48% 0.180 25 / 0.08); }
    .pill.leaf { color: var(--leaf); border-color: oklch(48% 0.110 145 / 0.35); background: oklch(48% 0.110 145 / 0.08); }
    .pill.saffron { color: var(--saffron-deep); border-color: oklch(58% 0.140 72 / 0.40); background: oklch(70% 0.150 78 / 0.12); }
    .btn {
      font-family: var(--mono);
      font-size: 11px;
      letter-spacing: 0.4em;
      text-transform: uppercase;
      padding: 14px 18px;
      background: var(--indigo);
      color: var(--parchment);
      border-radius: 2px;
      font-weight: 700;
      border: 0;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: transform 120ms cubic-bezier(0.32, 0.72, 0, 1), filter 120ms, box-shadow 200ms;
    }
    .btn::before {
      content: '';
      position: absolute;
      inset: 1px;
      border: 1px solid oklch(94% 0.022 78 / 0.35);
      border-radius: inherit;
      pointer-events: none;
    }
    .btn:hover { filter: brightness(1.08); }
    .btn:active { transform: scale(0.97); filter: brightness(0.94); }
    .btn.ghost {
      background: transparent;
      color: var(--indigo);
      border: 1px solid var(--rule-indigo);
    }
    .btn.ghost::before { display: none; }
    .btn.danger { background: var(--crimson); }
    .btn.tiny { font-size: 9.5px; padding: 9px 12px; letter-spacing: 0.32em; }
    .btn-row { display: flex; gap: 8px; flex-wrap: wrap; }
    input, textarea, select {
      font-family: var(--body);
      font-size: 15px;
      padding: 11px 13px;
      background: oklch(96% 0.018 78);
      border: 1px solid var(--rule-strong);
      border-radius: 2px;
      color: var(--ink);
      width: 100%;
      transition: border-color 200ms cubic-bezier(0.32, 0.72, 0, 1), background 200ms;
    }
    input:focus, textarea:focus, select:focus {
      border-color: var(--indigo);
      background: oklch(98% 0.015 78);
      outline: none;
    }
    label.field {
      display: grid;
      gap: 4px;
      font-family: var(--mono);
      font-size: 10px;
      letter-spacing: 0.28em;
      text-transform: uppercase;
      color: var(--ink-dim);
    }
    .row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    @container (max-width: 380px) {
      .row { grid-template-columns: 1fr; }
    }
    .list { display: grid; gap: 10px; margin-top: 16px; }
    .list-empty {
      font-family: var(--display);
      font-style: italic;
      color: var(--ink-faint);
      padding: 12px 0;
      text-align: center;
      font-size: 15px;
    }
    .list-row {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 8px;
      align-items: center;
      padding: 12px 14px;
      background: var(--parchment);
      border: 1px solid var(--rule-hair);
      border-left: 2px solid var(--indigo);
      border-radius: 2px;
    }
    .list-row.crimson { border-left-color: var(--crimson); }
    .list-row.leaf { border-left-color: var(--leaf); }
    .list-row .title {
      font-family: var(--display);
      font-weight: 600;
      font-size: 16px;
      margin-bottom: 2px;
    }
    .list-row .sub {
      font-family: var(--mono);
      font-size: 10px;
      color: var(--ink-faint);
      letter-spacing: 0.06em;
    }
    .list-row .meta {
      text-align: right;
      font-family: var(--mono);
      font-size: 12px;
      color: var(--indigo);
    }
    .form-grid {
      display: grid;
      gap: 12px;
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid var(--rule-hair);
    }
    [hidden] { display: none !important; }
  `;
  root.appendChild(style);
}
